import React, {
  ChangeEvent,
  Dispatch,
  ElementType,
  MouseEvent,
  SetStateAction,
  useRef,
  useState,
} from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  ButtonProps,
  ClickAwayListener,
  List,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography as T,
} from "@mui/material";
import { ButtonTypeMap } from "@mui/material/Button/Button";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import DomainList from "datasets/pages/settings/components/DomainList";
import SystemPromptEditor from "datasets/pages/settings/components/SystemPromptEditor";
import useConfiguratorContext from "datasets/pages/settings/contexts/configurator";
import useActiveConfig from "datasets/pages/settings/hooks/useActiveConfig";
import useConfigurations from "datasets/pages/settings/hooks/useConfigurations";
import useDefaultConfig from "datasets/pages/settings/hooks/useDefaultConfig";
import { format } from "date-fns";
import { mutate as globalMutate } from "swr";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";
import { PlusIcon } from "~/assets/icons";
import AbandonmentDialog, {
  ABANDONMENT_DEFAULT,
  AbandonmentDialogProps,
} from "~/components/AbandonmentDialog";
import ListCard from "~/components/ListCard";
import PageHeader from "~/components/PageHeader";
import useErrorToast from "~/contexts/error-toast";
import useCEFilterAgent from "~/pages/cohorts/components/assistant/hooks/useCEFilterAgent";
import CeConfigResponse, { CeConfig } from "~/types/interfaces/ce-config";
import rumClient from "~/utils/monitoring";

export const PUBLISH_TIMEOUT_MESSAGE =
  "A timeout has occurred. Configuration publishing may still be executing in the background — refresh in a few minutes to see your results.";

interface ConfigPublishResponse extends CeConfigResponse {
  message?: string;
}

// Safely parses the response body and throws on 504 or unparseable responses.
// Returns the parsed body for all other cases so the caller can handle 400 errors inline.
export async function parseConfigPublishResponse(resp: Response): Promise<ConfigPublishResponse> {
  const text = await resp.text();
  let data: ConfigPublishResponse;
  try {
    data = (text ? JSON.parse(text) : {}) as ConfigPublishResponse;
  } catch {
    if (!resp.ok) {
      throw new Error(
        resp.status === 504 ? PUBLISH_TIMEOUT_MESSAGE : "Unable to publish configuration."
      );
    }
    // Non-JSON 200 can happen if response came from proxy/gateway, not cohort-explorer
    throw new Error("Invalid response from server.");
  }
  if (resp.status === 504) {
    throw new Error(PUBLISH_TIMEOUT_MESSAGE);
  }
  return data;
}

const formatDateTime = (timestamp: string) => {
  const date = new Date(timestamp);
  const isCurrentYear = date.getFullYear() === new Date().getFullYear();
  return format(date, !isCurrentYear ? "PPp" : "LLLL d, hh:mm a");
};

interface SecondaryActionProps extends ButtonProps {
  component?: ElementType;
}

const SecondaryAction = ((props: SecondaryActionProps) => {
  return (
    <Button
      variant="contained"
      color="secondary"
      {...props}
      sx={{ bgcolor: "grey.200", "&:hover": { bgcolor: "grey.300" }, ...(props.sx ?? {}) }}
    />
  );
}) as OverridableComponent<ButtonTypeMap<SecondaryActionProps>>;

const ConfigList = ({
  disabled,
  setAbandonment,
}: {
  disabled: boolean;
  setAbandonment: Dispatch<SetStateAction<AbandonmentDialogProps>>;
}) => {
  const { data: configList } = useConfigurations();
  const [version, setVersion] = useQueryParam(
    "version",
    withDefault(NumberParam, configList?.[0]?.version_id)
  );
  const {
    config: currentConfig,
    editing: editingDraft,
    clearDraftConfig,
  } = useConfiguratorContext();
  const isLatest = configList?.[0]?.version_id === version;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleVersionClick = (config: CeConfig) => {
    const isLive = config.version_id === configList?.[0]?.version_id;

    if (editingDraft) {
      const clearAbandonment = () => setAbandonment(ABANDONMENT_DEFAULT);
      const loadVersion = () => {
        clearAbandonment();
        clearDraftConfig();
        setVersion(isLive ? undefined : config.version_id);
      };

      setAbandonment({
        open: true,
        title: "Unpublished Changes",
        message: `You will lose all unpublished changes when you change configuration versions. Would you like to continue switching versions?`,
        actions: {
          primary: {
            label: `Load Configuration v${config.version_id}`,
            callback: loadVersion,
          },
          secondary: {
            label: "Cancel",
            callback: clearAbandonment,
          },
        },
      });
    } else {
      setVersion(isLive ? undefined : config.version_id);
    }
  };

  if (!configList?.length) {
    return null;
  }

  return (
    <>
      <SecondaryAction disabled={disabled} endIcon={<ExpandMore />} onClick={handleMenuClick}>
        {editingDraft && "Draft (unpublished)"}
        {!editingDraft && `Version ${version} (${isLatest ? "latest" : "published"})`}
      </SecondaryAction>
      <Popper placement="bottom-end" open={!!anchorEl} anchorEl={anchorEl}>
        <ClickAwayListener onClickAway={handleMenuClose}>
          <Paper sx={{ overflowY: "auto", maxHeight: 360 }}>
            <List>
              {configList.map((config, i) => (
                <MenuItem
                  key={i}
                  disabled={!editingDraft && currentConfig?.version_id === config.version_id}
                  selected={!editingDraft && currentConfig?.version_id === config.version_id}
                  onClick={() => {
                    handleVersionClick(config);
                    handleMenuClose();
                  }}
                >
                  <ListItemText
                    sx={{ fontSize: 12 }}
                    primary={`v${config.version_id} · ${formatDateTime(config.created_at)}`}
                    secondary={config.created_by}
                    secondaryTypographyProps={{
                      sx: { color: "inherit", fontSize: "inherit", opacity: 0.6 },
                    }}
                  />
                </MenuItem>
              ))}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

const EmptyPlaceholder = ({
  handleUpload,
}: {
  handleUpload: (event: ChangeEvent<HTMLInputElement>) => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  return (
    <Stack height={500} alignItems="center" justifyContent="center">
      <T variant="label" textAlign="center" fontSize={16} lineHeight={2} mb={1.5} mt={-10}>
        Add a table to get started
        <br />
        or
      </T>
      <Button
        variant="contained"
        startIcon={<FileUploadOutlined />}
        onClick={() => fileInputRef.current?.click()}
      >
        Upload Config
      </Button>
      <input
        type="file"
        accept=".json,application/json"
        ref={fileInputRef}
        onChange={handleUpload}
        style={{ display: "none" }}
      />
    </Stack>
  );
};

const TablesListHeader = () => {
  const { config, loading: loadingConfig } = useConfiguratorContext();
  const { data: tableFQNs, isLoading: loadingTableFQNs } = useDatasetTableFQNs(false);
  const configuredTables = config?.content?.tables.map(({ table }) => table) ?? [];
  const createDisabled =
    loadingConfig || loadingTableFQNs || tableFQNs?.every((fqn) => configuredTables.includes(fqn));

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" minHeight={36.5} py={2}>
      <Box>
        <T component="h2" variant="inherit" fontWeight={500}>
          Tables
        </T>
        <T variant="body2" color="text.secondary" mt={0.5}>
          Configure tables from this dataset to support data exploration and cohort creation in
          Cohort Explorer.
        </T>
      </Box>
      <Tooltip arrow title={createDisabled ? "All available tables have been configured" : ""}>
        <span>
          <SecondaryAction
            component={RouterLink}
            to="new"
            disabled={createDisabled}
            startIcon={<PlusIcon />}
          >
            Add Table
          </SecondaryAction>
        </span>
      </Tooltip>
    </Box>
  );
};

const OverflowMenu = ({
  handleUpload,
}: {
  handleUpload: (event: ChangeEvent<HTMLInputElement>) => void;
}) => {
  const { data: activeConfig = {} } = useActiveConfig();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { config, editing, addDomain, removeDomain, clearDraftConfig } = useConfiguratorContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = () => {
    const jsonString = JSON.stringify(config, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${editing ? "draft" : "v" + config?.version_id}-config.json`;
    link.click();
    URL.revokeObjectURL(url);
    handleClose();
  };

  return (
    <>
      <SecondaryAction
        aria-label="Show configuration actions"
        id="config-menu-btn"
        aria-controls={open ? "config-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleOpen}
        sx={{ px: 0.75 }}
      >
        <MoreHoriz />
      </SecondaryAction>
      <Menu
        id="config-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ mt: 1 }}
        MenuListProps={{
          "aria-labelledby": "config-menu-btn",
        }}
      >
        <MenuItem
          key="upload"
          onClick={() => {
            fileInputRef.current?.click();
            handleClose();
          }}
        >
          Upload Config
        </MenuItem>
        <MenuItem
          key="download"
          disabled={!Object.keys(activeConfig ?? {}).length}
          onClick={handleDownload}
        >
          Download Config
        </MenuItem>
        <MenuItem
          key="reset"
          disabled={!editing}
          onClick={() => {
            clearDraftConfig();
            handleClose();
          }}
        >
          Reset Changes
        </MenuItem>
      </Menu>
      <input
        type="file"
        accept=".json,application/json"
        ref={fileInputRef}
        onChange={handleUpload}
        style={{ display: "none" }}
      />
    </>
  );
};

const ConfigOverview = () => {
  const { showError } = useErrorToast();
  const { datasetId } = useParams();
  const { data: defaultConfig } = useDefaultConfig();
  const ceFilterAgentEnabled = useCEFilterAgent();
  const [, setVersion] = useQueryParam("version", NumberParam);
  const {
    config: currentConfig,
    editing: editingDraft,
    loading: loadingCurrentConfig,
    shiftDomain,
    removeDomain,
    clearDraftConfig,
    setDraftConfig,
    setSubjectLabel,
  } = useConfiguratorContext();
  const { data: activeConfig, mutate: mutateActiveConfig } = useActiveConfig();
  const { data: configList, mutate: mutateConfigList } = useConfigurations();
  const [abandonment, setAbandonment] = useState<AbandonmentDialogProps>(ABANDONMENT_DEFAULT);
  const [publishing, setPublishing] = useState(false);
  const [validationError, setValidationError] = useState("");
  const isPublishEnabled = editingDraft || currentConfig?.version_id !== activeConfig?.version_id;
  const showEmpty = !currentConfig?.content?.tables.length;

  const handlePublish = async (config: Partial<CeConfigResponse>) => {
    setPublishing(true);
    setValidationError("");

    try {
      const formattedRequest = {
        dataset_id: datasetId,
        content: {
          tables: config?.content?.tables?.map((domain, i) => ({
            ...domain,
            primary: i === 0,
            columns: domain.columns ?? [],
          })),
          subject_label:
            config?.content?.subject_label ||
            defaultConfig?.content?.subject_label ||
            "Participants",
          llm_filter_agent_instructions: config?.content?.llm_filter_agent_instructions || "",
        },
      };

      const resp = await fetch(`/api/cohort-explorer/configurator/configs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formattedRequest),
      });

      const body = await parseConfigPublishResponse(resp);

      if (resp.ok) {
        clearDraftConfig();
        mutateActiveConfig(body);
        mutateConfigList([body, ...(configList ?? [])]);
        setVersion(undefined);
        // Invalidate domains for this dataset so DataVisualization gets fresh domains and can refetch cohort when needed
        if (datasetId) {
          globalMutate(
            (key) =>
              typeof key === "string" &&
              key.includes("/api/cohort-explorer/") &&
              key.includes(`dataset_id=${datasetId}`),
            undefined,
            { revalidate: true }
          );
        }
      } else {
        if (resp.status === 400 && body.message) {
          setValidationError(body.message);
        } else {
          throw Error("Unable to publish configuration");
        }
      }
    } catch (e: any) {
      console.error(e);
      showError(e.message, { persistent: true });
      rumClient?.recordError(e);
    }

    setPublishing(false);
  };

  const handlePublishAttempt = async () => {
    // Refresh active to see if anyone else has published changes before current user
    const activeLatest = await mutateActiveConfig();

    if (
      editingDraft &&
      (!currentConfig?.version_id || currentConfig.version_id === activeLatest?.version_id)
    ) {
      await handlePublish(currentConfig!);
    } else {
      const clearAbandonment = () => setAbandonment(ABANDONMENT_DEFAULT);
      const continuePublish = () => {
        clearAbandonment();
        handlePublish(currentConfig!);
      };

      setAbandonment({
        open: true,
        title: "Outdated Version",
        message:
          "You are about to publish an older version of the configuration. This will overwrite the latest version. Would you like to continue?",
        actions: {
          primary: {
            label: "Publish",
            callback: continuePublish,
          },
          secondary: {
            label: "Cancel",
            callback: clearAbandonment,
          },
        },
      });
    }
  };

  const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const data = e.target?.result;
        try {
          // Parse the file data
          const uploadedConfig: CeConfigResponse = JSON.parse(data as string);

          if (!uploadedConfig.content) {
            throw Error("Config content missing");
          }

          // Set both tables and prompt fields in a single operation
          const newContent = {
            tables: uploadedConfig.content.tables,
            subject_label: uploadedConfig.content.subject_label || "",
            llm_filter_agent_instructions:
              uploadedConfig.content?.llm_filter_agent_instructions || "",
          };

          setDraftConfig((prev) => ({
            ...prev,
            content: newContent,
          }));
        } catch (e) {
          console.error(e);
          showError("Unable to read config file");
        }
      };

      reader.onerror = (e) => {
        console.error("Error reading file: ", e);
        showError("Unable to read config file");
      };

      // Read the file as text
      reader.readAsText(file);
    }

    // Reset input value
    event.target.value = "";
  };

  return (
    <Stack gap={2}>
      {validationError && <Alert severity="error">{validationError}</Alert>}
      <Box display="flex" alignItems="center" gap={1} minHeight={36.5}>
        <PageHeader flex={1}>Cohort Explorer Configurator</PageHeader>
        <ConfigList disabled={publishing} setAbandonment={setAbandonment} />
        <>
          <LoadingButton
            variant="contained"
            onClick={handlePublishAttempt}
            loading={publishing}
            disabled={!isPublishEnabled}
          >
            Publish
          </LoadingButton>
          <OverflowMenu handleUpload={handleUpload} />
        </>
      </Box>
      <ListCard
        borderTop
        placeholder={{ height: 53 }}
        loading={loadingCurrentConfig}
        listStyles={{
          py: 0,
          px: 0,
        }}
        sx={{
          mb: 2,
          overflow: "visible",
        }}
        header={<TablesListHeader />}
      >
        {showEmpty ? (
          <EmptyPlaceholder handleUpload={handleUpload} />
        ) : (
          <DomainList
            domains={currentConfig?.content?.tables}
            handleShift={shiftDomain}
            handleRemove={removeDomain}
          />
        )}
      </ListCard>

      {/* Other sections */}
      {!showEmpty && (
        <>
          <Stack sx={{ gap: 2 }}>
            <Box>
              <T component="h2" variant="inherit" fontWeight={500}>
                Subject Type
              </T>
              <T variant="body2" color="text.secondary" mt={0.5}>
                Specifies the type of study subject (e.g., mice, participants) used to label and
                organize the data.
              </T>
            </Box>
            <OutlinedInput
              size="small"
              value={currentConfig?.content?.subject_label ?? defaultConfig?.content?.subject_label}
              onChange={(e) => setSubjectLabel(e.target.value)}
            />
          </Stack>

          <Box sx={{ mt: 2 }}>
            <SystemPromptEditor />
          </Box>
        </>
      )}

      <AbandonmentDialog {...abandonment} />
    </Stack>
  );
};

export default ConfigOverview;
